import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CardGrid, ModuleCard, Page } from '@rammetec/design-system'
import { ChevronRight, Home } from 'lucide-react'
import { useAvailableModules } from '@syscolect/application'
import { useSessionManager } from '@syscolect/application'
import { useCurrentProfile } from '@syscolect/application'
import type { ModuleInstance } from '@syscolect/domain'
import { useGroupChildren, useModuleBreadcrumbs } from './hierarchy'
import type { AppModule } from './registry'
import { useCurrentModule } from './CurrentModuleContext'
import { useInstanceDisplayMap } from './InstanceDisplayContext'

interface GroupLandingPageProps {
  instance: ModuleInstance
}

/**
 * Landing page for group-type modules (`/m/g?mid=<id>`).
 *
 * Renders:
 *  - Breadcrumbs: Home → parent groups → current group
 *  - A card grid of the group's direct children
 *    - Leaf children → navigate to their module page
 *    - Group children → navigate to their GroupLandingPage (recursive)
 *
 * Bounces to `/` when the org or profile changes while the user is inside a
 * group (mirrors C:\dev3 DashboardPage behaviour).
 */
export function GroupLandingPage({ instance }: GroupLandingPageProps) {
  const navigate = useNavigate()
  const { currentOrganizationId } = useSessionManager()
  const { profileData } = useCurrentProfile()
  const children = useGroupChildren(instance.id)
  const breadcrumbs = useModuleBreadcrumbs(instance.id)
  const modules = useAvailableModules<AppModule>()
  const mod = useCurrentModule()
  const instanceDisplay = useInstanceDisplayMap()

  const modulesBySlug = new Map(modules.map((m) => [m.slug, m]))

  // Bounce to root when org or profile changes while in a group
  const prevOrgRef = useRef<string | null | undefined>(currentOrganizationId)
  const prevProfileRef = useRef<string | undefined>(profileData?.id)
  useEffect(() => {
    const orgChanged =
      prevOrgRef.current !== undefined &&
      prevOrgRef.current !== currentOrganizationId
    const profileChanged =
      prevProfileRef.current !== undefined &&
      prevProfileRef.current !== profileData?.id
    prevOrgRef.current = currentOrganizationId
    prevProfileRef.current = profileData?.id
    if (orgChanged || profileChanged) {
      navigate('/', { replace: true })
    }
  }, [currentOrganizationId, profileData?.id, navigate])

  const currentCrumb = breadcrumbs[breadcrumbs.length - 1]
  const pageTitle = currentCrumb?.label ?? instance.label ?? instance.slug

  return (
    <Page
      name="console.group"
      title={pageTitle}
      icon={mod?.iconNode}
      description=""
      productArea="console"
    >
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-fg-muted mb-4">
          <button
            type="button"
            className="flex items-center gap-1 hover:text-fg transition-colors"
            onClick={() => navigate('/')}
          >
            <Home size={14} />
            <span>Início</span>
          </button>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1
            return (
              <span key={crumb.id} className="flex items-center gap-1">
                <ChevronRight size={14} className="shrink-0" />
                {isLast ? (
                  <span className="font-medium text-fg">{crumb.label}</span>
                ) : (
                  <button
                    type="button"
                    className="hover:text-fg transition-colors"
                    onClick={() => navigate(`/m/${crumb.slug}?mid=${crumb.id}`)}
                  >
                    {crumb.label}
                  </button>
                )}
              </span>
            )
          })}
        </nav>
      )}

      <CardGrid columns={{ sm: 2, lg: 3 }}>
        {children.map((child) => {
          const def = modulesBySlug.get(child.slug)
          const title = child.label ?? def?.label ?? child.slug
          // Per-instance display for groups (they share slug 'g'); slug-based
          // lookup is correct for leaf children.
          const display = instanceDisplay.get(child.id)
          return (
            <ModuleCard
              key={child.id}
              icon={display?.icon ?? def?.iconNode}
              title={title}
              description={display?.description ?? def?.description ?? ''}
              aria-label={`Abrir ${title}`}
              onClick={() => navigate(`/m/${child.slug}?mid=${child.id}`)}
            />
          )
        })}
      </CardGrid>
    </Page>
  )
}
